import React from "react";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import { SxProps, Theme } from "@mui/material/styles";

export const SPLIT_VIEW_DEFAULT_WIDTH = 540;
export const SPLIT_VIEW_DEFAULT_HEIGHT = 304;
export const SPLIT_VIEW_GRID_GAP = 2;
export const SPLIT_VIEW_ARROW_GUTTER = 70;
export const SPLIT_VIEW_MIN_WIDTH = 360;
export const SPLIT_VIEW_MIN_HEIGHT = 200;
export const SPLIT_VIEW_MAX_WIDTH = 960;
export const SPLIT_VIEW_MAX_HEIGHT = 540;

export const DEFAULT_VIDEO_LAYOUT_WIDTH = 680;

export interface SplitViewSize {
  width: number;
  height: number;
}

export const clampVideoPanelHeight = (height: number) =>
  Math.min(
    SPLIT_VIEW_MAX_HEIGHT,
    Math.max(SPLIT_VIEW_MIN_HEIGHT, Math.round(height))
  );

export const clampSplitViewSize = (
  width: number,
  height: number
): SplitViewSize => ({
  width: Math.min(
    SPLIT_VIEW_MAX_WIDTH,
    Math.max(SPLIT_VIEW_MIN_WIDTH, Math.round(width))
  ),
  height: Math.min(
    SPLIT_VIEW_MAX_HEIGHT,
    Math.max(SPLIT_VIEW_MIN_HEIGHT, Math.round(height))
  ),
});

export const getSplitPaneWidth = (totalWidth: number) =>
  (totalWidth - SPLIT_VIEW_GRID_GAP) / 2;

export const getVideoLayoutWidth = (
  contentWidth: number,
  useResizableLayout: boolean
) =>
  useResizableLayout
    ? contentWidth + SPLIT_VIEW_ARROW_GUTTER * 2
    : DEFAULT_VIDEO_LAYOUT_WIDTH;

/** Vertical center for nav arrows beside the resizable video block. */
export const getVideoPanelArrowTopPx = (videoHeight: number) =>
  Math.round(42 + videoHeight / 2 - 20);

/** @deprecated Use getVideoPanelArrowTopPx */
export const getSplitViewArrowTopPx = getVideoPanelArrowTopPx;

export interface ResizeHandleButtonProps {
  ariaLabel: string;
  cursorClass: string;
  iconClassName?: string;
  iconSx?: SxProps<Theme>;
  onMouseDown: (event: React.MouseEvent) => void;
}

export const ResizeHandleButton: React.FC<ResizeHandleButtonProps> = ({
  ariaLabel,
  cursorClass,
  iconClassName = "",
  iconSx,
  onMouseDown,
}) => (
  <button
    type="button"
    aria-label={ariaLabel}
    onMouseDown={onMouseDown}
    className={`flex h-8 w-8 ${cursorClass} items-center justify-center rounded-full border border-actuate-grey bg-white font-mulish text-actuate-blue shadow-sm transition-colors hover:border-actuate-orange hover:text-actuate-orange`}
  >
    <OpenInFullIcon
      fontSize="small"
      className={iconClassName}
      sx={iconSx}
      aria-hidden
    />
  </button>
);

interface SplitViewResizeHandleProps {
  size: SplitViewSize;
  onResize: (size: SplitViewSize) => void;
}

export const SplitViewResizeHandle: React.FC<SplitViewResizeHandleProps> = ({
  size,
  onResize,
}) => {
  const handleMouseDown = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    const startX = event.clientX;
    const startY = event.clientY;
    const startWidth = size.width;
    const startHeight = size.height;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      onResize(
        clampSplitViewSize(
          startWidth + (moveEvent.clientX - startX),
          startHeight + (moveEvent.clientY - startY)
        )
      );
    };

    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    document.body.style.cursor = "nwse-resize";
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  return (
    <ResizeHandleButton
      ariaLabel="Resize video panel"
      cursorClass="cursor-nwse-resize"
      iconClassName="-scale-x-100"
      onMouseDown={handleMouseDown}
    />
  );
};

interface VideoHeightResizeHandleProps {
  height: number;
  onHeightChange: (height: number) => void;
}

export const VideoHeightResizeHandle: React.FC<
  VideoHeightResizeHandleProps
> = ({ height, onHeightChange }) => {
  const handleMouseDown = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    const startY = event.clientY;
    const startHeight = height;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      onHeightChange(
        clampVideoPanelHeight(startHeight + (moveEvent.clientY - startY))
      );
    };

    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    document.body.style.cursor = "ns-resize";
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  return (
    <button
      type="button"
      aria-label="Resize video height"
      onMouseDown={handleMouseDown}
      className="flex h-8 w-8 cursor-ns-resize items-center justify-center rounded-full border border-actuate-grey bg-white font-mulish text-actuate-blue shadow-sm transition-colors hover:border-actuate-orange hover:text-actuate-orange"
    >
      <UnfoldMoreIcon fontSize="small" aria-hidden />
    </button>
  );
};
